/**
 * Единая страница входа рабочих кабинетов — lovirds.business.
 * На одной странице и бизнес (рекламодатель), и партнёр: переключатель сверху.
 *
 * Маршруты, которые её рендерят:
 *   /                    — по умолчанию бизнес
 *   /business/login      — бизнес (старые ссылки и письма)
 *   /partner/login       — партнёр (старые ссылки, редирект PartnerContext)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';

import { APIBusinessLogin } from '../../api_business_auth';
import { APIPartnerLogin } from '../../api_partner_auth';
import { useAdvertiser } from '../../AdvertiserContext';
import { usePartner } from '../../PartnerContext';
import { NAME_PROJECT } from '../../utils';
import {
    AuthBackground, Wordmark, Field, PrimaryButton, TextLink, Notice, PasswordToggle,
    IconMail, IconLock, IconCheck, IconMegaphone, IconHandshake,
} from '../../components/Auth/ui';
import '../../components/Auth/cabinet.css';

const ROLES = {
    business: {
        label: 'Бизнес',
        Icon: IconMegaphone,
        kicker: 'Кабинет рекламодателя',
        lead: 'Реклама в Lovirds',
        sub: 'Запускайте кампании на аудиторию, которая ищет отношения: тонкий таргетинг по городу, возрасту и интересам.',
        bullets: [
            'Кампании и креативы в одном кабинете',
            'Пополнение баланса и закрывающие документы',
            'Отчёты по показам, кликам и расходу',
        ],
        forgot: '/business/forgot-password',
    },
    partner: {
        label: 'Партнёр',
        Icon: IconHandshake,
        kicker: 'Кабинет партнёра',
        lead: 'Партнёрам Lovirds',
        sub: 'Приводите пользователей и рекламодателей, следите за начислениями и заказывайте выплаты.',
        bullets: [
            'Реферальная ссылка и статистика переходов',
            'Баланс, начисления и история выплат',
            'Реквизиты физлица или юрлица',
        ],
        forgot: '/partner/forgot-password',
    },
};
const ORDER = ['business', 'partner'];

export default function PortalLoginPage({ initialRole = 'business' }) {
    const navigate = useNavigate();
    const advertiser = useAdvertiser();
    const partner = usePartner();

    const [role, setRole] = useState(ORDER.includes(initialRole) ? initialRole : 'business');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [needVerify, setNeedVerify] = useState(false);

    const cfg = ROLES[role];

    // Уже вошли — сразу в кабинет, без повторного логина
    useEffect(() => {
        if (advertiser?.authStatus === 'authorized') navigate('/business', { replace: true });
        else if (partner?.authStatus === 'authed') navigate('/partner', { replace: true });
    }, [advertiser?.authStatus, partner?.authStatus, navigate]);

    const switchRole = (next) => {
        if (next === role) return;
        setRole(next);
        setError(null);
        setNeedVerify(false);
    };

    const submit = async (e) => {
        e?.preventDefault();
        setError(null);
        setNeedVerify(false);
        if (!email.trim() || !password) { setError('Введите email и пароль'); return; }

        setLoading(true);
        try {
            if (role === 'business') {
                const r = await APIBusinessLogin(email.trim(), password);
                advertiser.applyLoginSuccess(r.token, r.advertiser);
                navigate('/business');
            } else {
                const r = await APIPartnerLogin({ email: email.trim(), password });
                await partner.applyLoginSuccess({ token: r.token, partner: r.partner });
                navigate('/partner');
            }
        } catch (err) {
            if (err?.response?.status === 403 && err?.response?.data?.code === 'email_not_verified') {
                setNeedVerify(true);
            } else {
                setError(err?.response?.data?.error
                    || 'Не удалось войти. Проверьте почту и пароль или интернет-соединение');
            }
            setLoading(false);
        }
    };

    return (
        <AuthBackground variant="pro">
            <div className="lv-pro">
                <motion.aside className="lv-pro__aside"
                              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.45 }}>
                    <div className="lv-pro__head">
                        <span className="lv-pro__mark"><img src="/logo.png" alt={NAME_PROJECT} /></span>
                        <div>
                            <Wordmark size="sm" />
                            <div className="lv-pro__kicker">для бизнеса и партнёров</div>
                        </div>
                    </div>

                    <motion.h1 key={`${role}-lead`} className="lv-pro__lead"
                               initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                               transition={{ duration: 0.3 }}>
                        {cfg.lead}
                    </motion.h1>
                    <motion.p key={`${role}-sub`} className="lv-pro__sub"
                              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: 0.05 }}>
                        {cfg.sub}
                    </motion.p>

                    <ul className="lv-pro__bullets">
                        {cfg.bullets.map((text, i) => (
                            <motion.li key={`${role}-${text}`}
                                       initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                                       transition={{ duration: 0.3, delay: 0.08 + i * 0.06 }}>
                                <span className="lv-pro__tick"><IconCheck /></span>
                                <span>{text}</span>
                            </motion.li>
                        ))}
                    </ul>
                </motion.aside>

                <div className="lv-pro__inner">
                    {/* На мобильном логотип нужен и здесь — левой колонки нет */}
                    <motion.div className="lv-pro__head lv-pro__head--mobile"
                                initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}>
                        <span className="lv-pro__mark"><img src="/logo.png" alt={NAME_PROJECT} /></span>
                        <div>
                            <Wordmark size="sm" />
                            <div className="lv-pro__kicker">{cfg.kicker}</div>
                        </div>
                    </motion.div>

                    <motion.div className="lv-seg" role="tablist" aria-label="Тип кабинета"
                                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.06, duration: 0.35 }}>
                        <motion.span className="lv-seg__pill" aria-hidden="true"
                                     animate={{ left: role === 'business' ? '5px' : '50%' }}
                                     style={{ width: 'calc(50% - 5px)' }}
                                     transition={{ type: 'spring', stiffness: 420, damping: 34 }} />
                        {ORDER.map((id) => {
                            const R = ROLES[id];
                            return (
                                <button key={id} type="button" role="tab" aria-selected={role === id}
                                        className={`lv-seg__btn${role === id ? ' is-active' : ''}`}
                                        onClick={() => switchRole(id)}>
                                    <R.Icon /> {R.label}
                                </button>
                            );
                        })}
                    </motion.div>

                    <motion.form className="lv-pro__panel" onSubmit={submit} noValidate
                                 initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                                 transition={{ delay: 0.1, type: 'spring', stiffness: 260, damping: 26 }}>
                        <h2 className="lv-pro__title">Вход в {role === 'business' ? 'кабинет рекламодателя' : 'кабинет партнёра'}</h2>

                        {error ? <Notice tone="error">{error}</Notice> : null}
                        {needVerify ? (
                            <Notice>Подтвердите email по ссылке из письма — и заходите снова.</Notice>
                        ) : null}

                        <Field icon={<IconMail />} type="email" inputMode="email" autoComplete="email"
                               placeholder={role === 'business' ? 'company@mail.ru' : 'partner@mail.ru'}
                               value={email} enterKeyHint="next"
                               onChange={(e) => setEmail(e.target.value)} />

                        <Field icon={<IconLock />} type={showPw ? 'text' : 'password'} autoComplete="current-password"
                               placeholder="Пароль" value={password} enterKeyHint="go"
                               onChange={(e) => setPassword(e.target.value)}
                               end={<PasswordToggle shown={showPw} onToggle={() => setShowPw((s) => !s)} />} />

                        <TextLink small className="lv-pro__forgot" onClick={() => navigate(cfg.forgot)}>
                            Забыли пароль?
                        </TextLink>

                        <PrimaryButton type="submit" loading={loading}>
                            {loading ? 'Входим…' : 'Войти'}
                        </PrimaryButton>

                        {role === 'business' ? (
                            <div className="lv-pro__foot">
                                Ещё нет кабинета?{' '}
                                <TextLink small onClick={() => navigate('/business/register')}>
                                    Зарегистрировать компанию
                                </TextLink>
                            </div>
                        ) : (
                            <div className="lv-pro__note">
                                <IconHandshake />
                                <span>
                                    Кабинеты партнёров заводит администратор. Хотите стать партнёром —
                                    напишите нам, пришлём доступ на почту.
                                </span>
                            </div>
                        )}
                    </motion.form>

                    <div className="lv-pro__links">
                        <TextLink small onClick={() => { window.location.href = 'https://lovirds.store'; }}>
                            Я ищу знакомства →
                        </TextLink>
                    </div>
                </div>
            </div>
        </AuthBackground>
    );
}
